import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  fetchChatList,
  bumpUnreadForRoom,
  bumpRoomLastActivity,
  setOnlineUserIds,
  addOnlineUserId,
  removeOnlineUserId,
} from "../store/slices/chatSlice";
import { fetchNotificationsForUser } from "../store/slices/notificationSlice";
import { userIdSelector } from "../store/slices/authSlice";
import { store } from "../store/store";
import * as chatRealtimeService from "../services/chatRealtimeService";
import { logErrorDev } from "../utils/formatters";
import { CHAT_USER_EVENT_TYPES } from "../utils/constants";
import { CHAT_DEEP_LINK_EVENT, CHAT_PATH } from "../constants";
import { getChatOtherUserDisplayName } from "../helpers/chatHelper";
import {
  extractSenderNameFromNotificationEvent,
  scheduleNewChatMessageToast,
} from "../helpers/chatMessageToast";

/**
 * Subscribes to user-events and global-presence as soon as the user is logged in
 * (Ably client must already exist — initialized in App after login).
 */
export function useChatAblySubscriptions(enabled: boolean) {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userIdSelector);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !userId) {
      chatRealtimeService.leaveGlobalPresence();
      chatRealtimeService.disconnect();
      dispatch(setOnlineUserIds([]));
      return;
    }

    let unsubNotifications: (() => void) | null = null;
    let unsubGlobalPresence: (() => void) | null = null;

    const setup = async () => {
      try {
        await chatRealtimeService.connect(userId);
        await chatRealtimeService.enterGlobalPresence();
        const snapshot = await chatRealtimeService.getGlobalPresenceMembers();
        if (mountedRef.current) {
          dispatch(setOnlineUserIds(snapshot));
        }

        unsubGlobalPresence = await chatRealtimeService.subscribeGlobalPresence(
          (ev) => {
            const id = ev.clientId;
            if (!id) {
              return;
            }
            const action = ev.action || "";
            if (action === "leave") {
              dispatch(removeOnlineUserId(id));
            } else if (
              action === "enter" ||
              action === "present" ||
              action === "update"
            ) {
              dispatch(addOnlineUserId(id));
            }
          }
        );

        unsubNotifications = await chatRealtimeService.subscribeUserNotifications(
          userId,
          (msg) => {
            void dispatch(
              fetchNotificationsForUser({ userId, silent: true })
            );

            const parsed =
              chatRealtimeService.parseChatUserNotificationEvent(msg.data);
            if (!parsed) {
              return;
            }

            if (parsed.kind === CHAT_USER_EVENT_TYPES.NEW_CHAT) {
              void dispatch(fetchChatList({ silent: true }));
              return;
            }

            const roomId = parsed.roomId;
            if (!roomId) {
              void dispatch(fetchChatList({ silent: true }));
              return;
            }
            const pathname =
              typeof window !== "undefined" ? window.location.pathname : "";
            const viewingRoomId = pathname.startsWith(`${CHAT_PATH}/`)
              ? decodeURIComponent(
                pathname.slice(CHAT_PATH.length + 1).split("/")[0] || ""
              )
              : null;
            if (viewingRoomId === roomId) {
              return;
            }
            dispatch(bumpUnreadForRoom(roomId));
            const hasRoom = store
              .getState()
              .chat.chats.some((chat) => chat.id === roomId);
            if (hasRoom) {
              dispatch(bumpRoomLastActivity(roomId));
            } else {
              void dispatch(fetchChatList({ silent: true }));
            }

            const chat = store
              .getState()
              .chat.chats.find((entry) => entry.id === roomId);
            const senderName =
              extractSenderNameFromNotificationEvent(msg.data) ||
              (chat ? getChatOtherUserDisplayName(chat) : undefined);
            const chatPath = `${CHAT_PATH}/${encodeURIComponent(roomId)}`;

            scheduleNewChatMessageToast(roomId, {
              senderName,
              onClick: () => {
                window.dispatchEvent(
                  new CustomEvent(CHAT_DEEP_LINK_EVENT, {
                    detail: { path: chatPath },
                  })
                );
              },
            });
          }
        );
      } catch (err) {
        logErrorDev("[ChatAbly] bootstrap failed", err);
      }
    };

    void setup();

    return () => {
      unsubNotifications?.();
      unsubGlobalPresence?.();
      void chatRealtimeService.leaveGlobalPresence();
    };
  }, [dispatch, enabled, userId]);
}
